import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import api from '@/lib/api';
import { toast } from '@/lib/toast';
import { useBranding } from '@/lib/branding';
import ColorField from '@/components/ColorField';

const DARK_FIELDS = [
  ['primary_color', 'Primary'],
  ['primary_contrast', 'Primary text'],
  ['secondary_color', 'Secondary'],
  ['error_color', 'Error'],
  ['warning_color', 'Warning'],
];

const LIGHT_FIELDS = [
  ['primary_color_light', 'Primary'],
  ['primary_contrast_light', 'Primary text'],
  ['secondary_color_light', 'Secondary'],
  ['error_color_light', 'Error'],
  ['warning_color_light', 'Warning'],
];

const ALL_KEYS = [...DARK_FIELDS, ...LIGHT_FIELDS].map(([k]) => k);

const BrandingColorSettings = () => {
  const { refresh } = useBranding();
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/branding');
        if (cancelled) return;
        const picked = {};
        ALL_KEYS.forEach((k) => {
          picked[k] = data[k];
        });
        setValues(picked);
      } catch (err) {
        toast.error('Failed to load branding');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setField = (key) => (val) => setValues((prev) => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/branding', values);
      await refresh();
      toast.success('Colors saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save colors');
    } finally {
      setSaving(false);
    }
  };

  if (!values) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Dark mode
        </Typography>
        <Grid container spacing={2}>
          {DARK_FIELDS.map(([key, label]) => (
            <Grid item xs={6} sm={4} key={key}>
              <ColorField label={label} value={values[key]} onChange={setField(key)} />
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Light mode
        </Typography>
        <Grid container spacing={2}>
          {LIGHT_FIELDS.map(([key, label]) => (
            <Grid item xs={6} sm={4} key={key}>
              <ColorField label={label} value={values[key]} onChange={setField(key)} />
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box>
        <Button variant="contained" onClick={handleSave} disabled={saving} data-testid="save-colors-button">
          {saving ? 'Saving…' : 'Save colors'}
        </Button>
      </Box>
    </Stack>
  );
};

export default BrandingColorSettings;
